/*
* MAINTENANCE ADVICE:
* This file is a MOCK version of ReviewPage.jsx. 
* Please keep this file in sync with ReviewPage.jsx. 
* Any UI changes, styling updates, or logic improvements made to the live page 
* should be reflected here to ensure the mock experience remains accurate.
*/

import React, { useState, useEffect } from 'react';
import axios from 'axios';

// Ensure cookies are sent with every request
axios.defaults.withCredentials = true;
const API_BASE = "https://api.zapexpenses.com";
import { CATEGORY_OPTIONS } from './constants';

// Helper to create a unique key for each receipt (id + receipt_index)
const getReceiptKey = (receipt) => `${receipt.id}-${receipt.receipt_index}`;

const DEFAULT_FIELDS = ['date', 'merchant_name', 'category', 'total', 'vat', 'currency'];

const MockReviewPage = () => {
    const [receipts, setReceipts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState('');
    const [imageUrls, setImageUrls] = useState({}); // id -> blobURL
    const [loadingImages, setLoadingImages] = useState({}); // id -> bool
    const [selectedImage, setSelectedImage] = useState(null); // Receipt object for modal
    const [isZoomed, setIsZoomed] = useState(false); // Zoom state for modal

    // Parse URL query parameter for single-receipt filtering
    const filterId = new URLSearchParams(window.location.search).get('id');

    // Handle History/Back Button for Lightbox
    useEffect(() => {
        if (selectedImage) {
            window.history.pushState({ lightbox: true }, '');
            const handlePopState = () => {
                setSelectedImage(null);
                setIsZoomed(false);
            };
            window.addEventListener('popstate', handlePopState);
            return () => window.removeEventListener('popstate', handlePopState);
        }
    }, [selectedImage]);

    // Reset zoom when opening a new image
    useEffect(() => {
        if (selectedImage) setIsZoomed(false);
    }, [selectedImage]);

    // 1. Fetch the "Needs Review" list on load
    useEffect(() => {
        fetchReceipts();
    }, []);

    const fetchReceipts = async () => {
        // Simulating API delay
        setTimeout(() => {
            const mockReceipts = [
                {
                    id: 101,
                    receipt_index: 0,
                    created_at: new Date().toISOString(),
                    source: 'email',
                    values: {
                        total: 7.00,
                        merchant_name: 'Poundland Limited',
                        vat: 1.16,
                        category: 'office',
                        date: '2025-11-26',
                        currency: 'GBP'
                    }
                },
                {
                    id: 102,
                    receipt_index: 0,
                    created_at: new Date(Date.now() - 86400000).toISOString(),
                    source: 'whatsapp',
                    values: {
                        total: 10.50,
                        merchant_name: 'Tesco Express',
                        vat: 0.00,
                        date: null,
                        currency: 'GBP'
                    }
                },
                {
                    // Fully empty receipt — tests default field rendering + "Select Category..." state
                    id: 103,
                    receipt_index: 0,
                    created_at: new Date(Date.now() - 3600000).toISOString(),
                    source: 'manual',
                    values: {}
                }
            ];

            let mockData = [];
            if (filterId) {
                mockData = mockReceipts.filter(r => String(r.id) === filterId);
            } else {
                mockData = mockReceipts;
            }

            setReceipts(mockData);
            setLoading(false);
        }, 600);
    };

    // Auto-fetch images when receipts are loaded
    useEffect(() => {
        receipts.forEach(receipt => {
            if (!imageUrls[receipt.id] && !loadingImages[receipt.id]) {
                fetchImage(receipt);
            }
        });
    }, [receipts]);

    const fetchImage = async (receipt) => {
        setLoadingImages(prev => ({ ...prev, [receipt.id]: true }));
        try {
            const res = await fetch('/mock_receipt.jpg');
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            setImageUrls(prev => ({ ...prev, [receipt.id]: url }));
        } catch (err) {
            console.error("Failed to fetch image", err);
        } finally {
            setLoadingImages(prev => ({ ...prev, [receipt.id]: false }));
        }
    };

    // Count how many receipts share the same image id (for "Receipt X of Y" display)
    const getReceiptCountForId = (id) => receipts.filter(r => r.id === id).length;
    const getReceiptPositionForId = (receipt) => {
        const sameIdReceipts = receipts.filter(r => r.id === receipt.id);
        return sameIdReceipts.findIndex(r => r.receipt_index === receipt.receipt_index) + 1;
    };

    // 2. Update a specific field in the local state's "values" object
    const handleInputChange = (id, receiptIndex, field, value) => {
        setReceipts(receipts.map(r =>
            (r.id === id && r.receipt_index === receiptIndex) ? {
                ...r,
                values: { ...r.values, [field]: value }
            } : r
        ));
    };

    // 3. POST the fix to the backend (mock)
    const submitFix = async (receipt) => {
        alert(`Fixed receipt ${receipt.id}! In prod this posts to API.`);
        setReceipts(receipts.filter(r => !(r.id === receipt.id && r.receipt_index === receipt.receipt_index)));
    };

    // Helper map for user-friendly labels
    const FIELD_LABELS = {
        total: "Total Amount",
        vat: "VAT Amount",
        merchant_name: "Merchant",
        category: "Category",
        date: "Date",
        currency: "Currency"
    };

    // Redirect when review is complete for a specific item
    useEffect(() => {
        if (!loading && filterId && receipts.length === 0) {
            const timer = setTimeout(() => {
                window.location.href = '/mockreceipts';
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [loading, filterId, receipts]);

    if (loading) return <div className="p-8 text-center text-slate-400">Loading receipts...</div>;

    if (filterId && receipts.length === 0) {
        return (
            <div className="p-8 text-center bg-slate-900 border border-slate-800 rounded-xl max-w-lg mx-auto mt-10">
                <div className="text-4xl mb-4">✅</div>
                <h2 className="text-xl font-bold text-white mb-2">Review Complete</h2>
                <p className="text-slate-400 text-sm">Redirecting back to receipts...</p>
            </div>
        );
    }

    return (
        <div className={`max-w-4xl mx-auto p-6 ${filterId ? 'pb-32' : ''}`}>
            <h1 className="text-2xl font-bold mb-6">Review Expenses (MOCK)</h1>

            {message && <p className="text-red-500 mb-4">{message}</p>}

            {receipts.length === 0 && !message ? (
                <div className="bg-indigo-500/10 border border-indigo-500/20 text-indigo-200 p-6 rounded-xl text-center">
                    🎉 All caught up! No receipts need review.
                </div>
            ) : (
                <div className="space-y-4">
                    {filterId && receipts.some(r => String(r.id) === filterId) && (
                        <div className="mb-4 bg-slate-900/50 p-4 rounded-lg border border-indigo-500/30 text-center">
                            <span className="text-indigo-200 text-sm">
                                Reviewing specific receipt (ID: {filterId})
                            </span>
                        </div>
                    )}

                    {receipts
                        .filter(receipt => filterId ? String(receipt.id) === filterId : true)
                        .map(receipt => {
                            const totalForId = getReceiptCountForId(receipt.id);
                            const position = getReceiptPositionForId(receipt);
                            const showMultiIndicator = totalForId > 1;
                            const hasValues = receipt.values && Object.keys(receipt.values).length > 0;
                            const fieldsToRender = hasValues ? Object.keys(receipt.values) : DEFAULT_FIELDS;

                            return (
                                <div key={getReceiptKey(receipt)} className="border border-slate-800 rounded-xl p-6 bg-slate-900 shadow-sm flex flex-col md:flex-row gap-6 items-start">
                                    <div className="flex-1">
                                        <p className="text-sm text-slate-500 mb-1">
                                            Captured on {new Date(receipt.created_at).toLocaleDateString()} by {receipt.source}
                                        </p>
                                        <div className="p-3 bg-black/30 rounded border border-white/5 font-mono text-xs text-slate-400 break-all">
                                            ID: {receipt.id}
                                            {showMultiIndicator && (
                                                <span className="ml-2 px-2 py-0.5 bg-indigo-600/30 text-indigo-300 rounded text-xs">
                                                    Receipt {position} of {totalForId}
                                                </span>
                                            )}
                                        </div>

                                        <div className="mt-4 rounded-lg overflow-hidden border border-slate-700 bg-black/50 relative flex items-center justify-center transition-all duration-300 group">
                                            {loadingImages[receipt.id] ? (
                                                <div className="text-slate-500 text-xs animate-pulse p-4">Loading image...</div>
                                            ) : imageUrls[receipt.id] ? (
                                                <div className="relative cursor-zoom-in w-full" onClick={() => setSelectedImage(receipt)}>
                                                    <img
                                                        src={imageUrls[receipt.id]}
                                                        alt="Receipt"
                                                        className="w-full max-h-64 object-contain opacity-90 group-hover:opacity-100 transition-opacity"
                                                    />
                                                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/20">
                                                        <span className="bg-black/70 text-white text-xs px-2 py-1 rounded-full backdrop-blur-sm">Click to Zoom</span>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="text-red-400 text-xs p-4">Image unavailable</div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex-1 space-y-3 w-full">
                                        <h3 className="text-xs font-bold text-indigo-400 uppercase tracking-wide mb-2">Needs Verification</h3>
                                        {!hasValues && <div className="text-xs text-amber-500 mb-2">No data extracted. Please enter details manually.</div>}

                                        {fieldsToRender.map(field => (
                                            <div key={field}>
                                                <label className="block text-xs font-semibold text-slate-400 mb-1">
                                                    {FIELD_LABELS[field] || field}
                                                </label>
                                                {field === 'category' ? (
                                                    <select
                                                        value={receipt.values?.[field] || ''}
                                                        onChange={(e) => handleInputChange(receipt.id, receipt.receipt_index, field, e.target.value)}
                                                        className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-white focus:outline-none focus:border-indigo-500 transition-colors"
                                                    >
                                                        <option value="" disabled>Select Category...</option>
                                                        {CATEGORY_OPTIONS.map(opt => (
                                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                        ))}
                                                    </select>
                                                ) : (
                                                    <input
                                                        type={field.includes('date') ? 'date' : 'text'}
                                                        value={receipt.values?.[field] ?? ''}
                                                        onChange={(e) => handleInputChange(receipt.id, receipt.receipt_index, field, e.target.value)}
                                                        className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-white focus:outline-none focus:border-indigo-500 transition-colors"
                                                    />
                                                )}
                                            </div>
                                        ))}

                                        <button
                                            onClick={() => submitFix(receipt)}
                                            className="w-full mt-4 bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700 transition font-semibold"
                                        >
                                            Approve &amp; Save (Mock)
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                </div>
            )}

            {filterId && (
                <div className="fixed bottom-0 left-0 right-0 p-4 bg-slate-900/90 backdrop-blur border-t border-slate-800 flex justify-center z-40">
                    <div className="max-w-4xl w-full flex justify-between items-center px-2">
                        <span className="text-slate-400 text-sm hidden sm:block">Viewing single receipt</span>
                        <button
                            onClick={() => window.location.href = '/mockreview'}
                            className="w-full sm:w-auto bg-indigo-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-indigo-500 transition shadow-lg shadow-indigo-500/20 flex items-center justify-center gap-2"
                        >
                            <span>Review All Pending ({receipts.length})</span>
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                            </svg>
                        </button>
                    </div>
                </div>
            )}

            {selectedImage && (
                <div
                    className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center animate-in fade-in duration-200"
                    onClick={() => window.history.back()}
                >
                    <button
                        className="fixed top-4 right-4 bg-black/50 text-white p-2 z-[60] rounded-full hover:bg-black/70 backdrop-blur-md transition-all"
                        onClick={(e) => {
                            e.stopPropagation();
                            window.history.back();
                        }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-8 h-8">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>

                    <div className={`w-full h-full overflow-auto p-4 ${isZoomed ? '' : 'flex items-center justify-center'}`}>
                        <img
                            src={imageUrls[selectedImage.id]}
                            alt="Zoomed Receipt"
                            className={`rounded shadow-2xl transition-all duration-300 ${isZoomed
                                ? 'w-full max-w-none cursor-zoom-out'
                                : 'max-h-full max-w-full object-contain cursor-zoom-in'
                                }`}
                            onClick={(e) => {
                                e.stopPropagation();
                                setIsZoomed(!isZoomed);
                            }}
                        />
                    </div>

                    <div className="fixed bottom-6 left-0 right-0 text-center pointer-events-none z-50">
                        <span className="bg-black/70 text-white/90 text-sm px-4 py-2 rounded-full backdrop-blur-md border border-white/10 shadow-lg">
                            Captured on {new Date(selectedImage.created_at).toLocaleDateString()} by {selectedImage.source}
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MockReviewPage;
